import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { ShieldCheck, X } from "lucide-react";
import { PADPULSE_PROMISE } from "@shared/padpulse-promise";
import { useIsV3Billing } from "@/hooks/use-v3-billing";

interface PlanTrustState {
  accountKind?: string | null;
  trial?: { active: boolean; daysLeft: number };
  guarantee?: { eligible: boolean; eligibleUntil: string | null; used: boolean };
}

type Phase = "trial" | "guarantee";

const dismissKey = (phase: Phase) => `padpulse-promise-banner:${phase}`;

/**
 * The persistent in-app trust signal for The PadPulse Promise, rendered in
 * the app shell's global banner zone. Two phases, each dismissible on its
 * own key (dismissing the trial banner must not suppress the later
 * guarantee-active banner):
 *   trial     → countdown + the guarantee that follows (trialBadge)
 *   guarantee → the first 30 paid days' refund window (activeBadge)
 * Renders nothing outside those windows or for legacy accounts.
 */
export function PromiseTrustBanner() {
  const planQuery = useQuery<PlanTrustState>({
    queryKey: ["/api/app/account/plan"],
    retry: false,
    staleTime: 60_000,
  });
  const [dismissedTick, setDismissedTick] = useState(0);
  // v3 per-property accounts: monthly plans have no money-back window, and
  // the v3 app-shell banner owns trial messaging.
  const isV3 = useIsV3Billing();

  const data = planQuery.data;
  if (isV3 || !data?.accountKind) return null;

  const phase: Phase | null = data.trial?.active
    ? "trial"
    : data.guarantee?.eligible && !data.guarantee.used
      ? "guarantee"
      : null;
  if (!phase) return null;

  void dismissedTick;
  if (typeof window !== "undefined" && localStorage.getItem(dismissKey(phase)) === "1") return null;

  const message =
    phase === "trial"
      ? PADPULSE_PROMISE.trialBadge(data.trial!.daysLeft)
      : PADPULSE_PROMISE.activeBadge(
          data.guarantee!.eligibleUntil
            ? new Date(data.guarantee!.eligibleUntil).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })
            : "",
        );

  return (
    <div
      className="flex items-center gap-2 bg-emerald-50 border-b border-emerald-200 px-4 py-1.5 text-sm text-emerald-800"
      data-testid="banner-promise-trust"
    >
      <ShieldCheck size={14} className="shrink-0 text-emerald-600" />
      <Link href="/app/account/billing" className="hover:underline min-w-0 truncate">
        {message}
      </Link>
      <button
        type="button"
        className="ml-auto shrink-0 text-emerald-600 hover:text-emerald-800"
        aria-label="Dismiss"
        onClick={() => {
          localStorage.setItem(dismissKey(phase), "1");
          setDismissedTick((t) => t + 1);
        }}
        data-testid="button-dismiss-promise-banner"
      >
        <X size={14} />
      </button>
    </div>
  );
}
